import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, EnvelopeSimple, MapPin, CheckCircle, ArrowRight, FacebookLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import Reveal from "@/components/site/Reveal";
import IntakeChat from "@/components/site/IntakeChat";
import { PHONE, PHONE_TEL, EMAIL, PROJECT_TYPES, FACEBOOK_URL } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", project_type: "", location: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [leadId, setLeadId] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.project_type) {
      toast.error("Please fill in your name, phone, email and project type.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      setLeadId(data.id);
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + PHONE + ".");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/20 text-white placeholder-white/35 px-4 py-3.5 text-sm focus:outline-none focus:border-gold transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-ink">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          <div>
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-5">Contact B3</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-heading font-bold tracking-tight text-white text-4xl sm:text-5xl leading-[1.05]">
                Tell us about your project.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-7 text-base sm:text-lg text-white/70 leading-relaxed max-w-lg">
                Call, email or use the form to request an estimate. We'll get the conversation started.
              </p>
            </Reveal>

            <div className="mt-10 space-y-4">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="contact-phone-link"
                className="group flex items-center gap-4 border border-line p-5 hover:border-gold transition-colors"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-gold/10 text-gold shrink-0">
                  <Phone weight="fill" size={22} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-white/45">Phone</span>
                  <span className="block text-lg font-heading text-white group-hover:text-gold transition-colors">{PHONE}</span>
                </span>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-link"
                className="group flex items-center gap-4 border border-line p-5 hover:border-gold transition-colors"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-gold/10 text-gold shrink-0">
                  <EnvelopeSimple weight="fill" size={22} />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs uppercase tracking-wider text-white/45">Email</span>
                  <span className="block text-lg font-heading text-white group-hover:text-gold transition-colors break-all">{EMAIL}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 border border-line p-5">
                <span className="flex h-12 w-12 items-center justify-center bg-gold/10 text-gold shrink-0">
                  <MapPin weight="fill" size={22} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-white/45">Service Area</span>
                  <span className="block text-sm text-white/80">Schertz • New Braunfels • San Antonio and surrounding areas</span>
                </span>
              </div>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook-link"
                className="group flex items-center gap-4 border border-line p-5 hover:border-gold transition-colors"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-gold/10 text-gold shrink-0">
                  <FacebookLogo weight="fill" size={22} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-white/45">Facebook</span>
                  <span className="block text-lg font-heading text-white group-hover:text-gold transition-colors">Follow &amp; read our reviews</span>
                </span>
              </a>
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="bg-surface border border-line p-7 sm:p-9">
              {submitted ? (
                <motion.div
                  data-testid="contact-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col items-start"
                >
                  <CheckCircle size={48} weight="duotone" className="text-gold" />
                  <h3 className="mt-4 font-heading font-bold text-2xl text-white tracking-tight">Request received.</h3>
                  <p className="mt-3 text-white/70 leading-relaxed">
                    Thank you for contacting B3 Contracting LLC. While you're here, our assistant will
                    grab a few quick details so Blake can call you prepared.
                  </p>
                  {leadId && <div className="w-full"><IntakeChat leadId={leadId} /></div>}
                  <button
                    data-testid="contact-reset"
                    onClick={() => { setSubmitted(false); setLeadId(null); }}
                    className="mt-6 inline-flex items-center gap-2 text-gold font-semibold hover:text-gold-hover transition-colors"
                  >
                    Send another request
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={onSubmit} data-testid="estimate-form" className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="form-name" name="name" autoComplete="name" className={inputCls} placeholder="Name" value={form.name} onChange={update("name")} />
                    <input data-testid="form-phone" name="phone" type="tel" autoComplete="tel" className={inputCls} placeholder="Phone" value={form.phone} onChange={update("phone")} />
                  </div>
                  <input data-testid="form-email" name="email" type="email" autoComplete="email" className={inputCls} placeholder="Email" value={form.email} onChange={update("email")} />
                  <select
                    data-testid="form-project-type"
                    className={`${inputCls} appearance-none cursor-pointer ${form.project_type ? "text-white" : "text-white/35"}`}
                    value={form.project_type}
                    onChange={update("project_type")}
                  >
                    <option value="" disabled className="bg-surface text-white/50">Project Type</option>
                    {PROJECT_TYPES.map((p) => (
                      <option key={p} value={p} className="bg-surface text-white">{p}</option>
                    ))}
                  </select>
                  <input data-testid="form-location" name="address" autoComplete="street-address" className={inputCls} placeholder="Project Address or City" value={form.location} onChange={update("location")} />
                  <textarea
                    data-testid="form-message"
                    rows={4}
                    className={`${inputCls} resize-none`}
                    placeholder="Tell us about your project"
                    value={form.message}
                    onChange={update("message")}
                  />
                  <button
                    type="submit"
                    data-testid="form-submit"
                    disabled={submitting}
                    className="group w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-8 py-4 hover:bg-gold-hover transition-colors duration-200 disabled:opacity-60"
                  >
                    {submitting ? "Sending..." : "Request Free Estimate"}
                    {!submitting && <ArrowRight size={18} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
